import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { CurrentUserService, TenantRequirement } from '@/app/core/current-user/current-user.service';

/**
 * The value of `param` on the route or the nearest ancestor that has it. A hub tab route (`tenanti/:tenantId/korisnici`)
 * does not inherit its parent's params under the router's default `paramsInheritanceStrategy`, so the tenant id is
 * looked up the tree instead.
 */
export function routeParamUpTheTree(route: ActivatedRouteSnapshot, param: string): string | null {
    for (let current: ActivatedRouteSnapshot | null = route; current; current = current.parent) {
        const value = current.paramMap.get(param);
        if (value) return value;
    }
    return null;
}

/**
 * Route guard factory for a route bound to one tenant: allows only when the actor meets EVERY given requirement in the
 * tenant named by the route's `:tenantId` — each requirement being ANY of its global specs or ANY of its tenant specs in
 * that tenant, per the server capability (`CurrentUserService.canInTenant`). Several requirements are for a page that
 * reads through more than one guarded area (the API clients hang under the leases). Refusal and a route without a
 * tenant id both go to `/zabranjen-pristup`.
 *
 * A global spec decides without loading anything; otherwise the capability is loaded once (cached per tenant) before
 * the decision. Usage: `canActivate: [requireTenantPermission(TENANT_CONTROLS.viewUsers)]`.
 */
export function requireTenantPermission(...requirements: readonly TenantRequirement[]): CanActivateFn {
    return async (route) => {
        // inject() only works synchronously, before the first await.
        const currentUser = inject(CurrentUserService);
        const router = inject(Router);
        const denied = router.createUrlTree(['/zabranjen-pristup']);

        const tenantId = routeParamUpTheTree(route, 'tenantId');
        if (!tenantId || requirements.length === 0) return denied;

        try {
            await currentUser.ensureLoaded();
        } catch {
            return denied;
        }
        if (currentUser.canAllInTenant(tenantId, requirements)) return true;

        await currentUser.ensureTenantCapability(tenantId);
        return currentUser.canAllInTenant(tenantId, requirements) ? true : denied;
    };
}
